import { getWeightedAverage } from '@mentimeter/visualizations-react/visualizations/scales/helpers';
import type { ScalesResultT } from '@mentimeter/visualizations';
import { useTranslations } from 'next-intl';
import type { ScalesRange } from '../types';

interface ScreenReaderResultsProps {
  results: ScalesResultT[];
  range: ScalesRange;
  dimensionLabels: { min: string; max: string };
  shouldHideResults: boolean;
}

export const ScreenReaderResults = ({
  results,
  dimensionLabels,
  range,
  shouldHideResults,
}: ScreenReaderResultsProps) => {
  const translate = useTranslations('presenting.screenreader_table');

  if (shouldHideResults) {
    return <span>{translate('results_are_hidden_message')}</span>;
  }

  return (
    <table>
      <caption>{translate('results_table_caption')}</caption>
      <thead>
        <tr>
          <th scope="col">{translate('options_column_title')}</th>
          <th scope="col">
            {translate('scales_average_value_column_title', {
              min: `${range.min}`,
              minLabel: dimensionLabels.min,
              max: `${range.max}`,
              maxLabel: dimensionLabels.max,
            })}
          </th>
        </tr>
      </thead>
      <tbody>
        {results.map((result) => {
          const hasOnlyZeros = Object.values(result.score).every(
            (score) => score === 0,
          );

          if (hasOnlyZeros) {
            return (
              <tr key={result.id}>
                <th scope="row">{result.label}</th>
                <td>{translate('no_results_message')}</td>
              </tr>
            );
          }

          const average = getWeightedAverage(result.score);
          return (
            <tr key={result.id}>
              <td>{result.label}</td>
              <td>{average.toFixed(1)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
